import React, {createContext, useContext, useEffect, useRef} from 'react';

type Command = {
  key?: string | string[];
  code?: string | string[];
  ctrlKey?: boolean;
  shiftKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  callback: (event: KeyboardEvent) => void;
};

const CommandContext = createContext<{
  register: (cmd: Command) => () => void;
} | null>(null);

export const KeyboardCommandProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const commandsRef = useRef<Command[]>([]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      for (const cmd of commandsRef.current) {
        const codes = cmd.code ? (Array.isArray(cmd.code) ? cmd.code : [cmd.code]) : [];
        const keys = cmd.key ? (Array.isArray(cmd.key) ? cmd.key : [cmd.key]) : [];
        const codeMatch = codes.length > 0 ? codes.includes(event.code) : false;
        const keyMatch = keys.length > 0 ? keys.includes(event.key) : false;
        const tagName = (event.target as HTMLElement).tagName.toLowerCase();
        const isInput = tagName === 'input' && (event.target as HTMLInputElement).type === 'text';
        const isTextarea = tagName === 'textarea';

        // Prefer code (event.code) for layout-independent shortcuts (e.g., physical key location),
        // otherwise fall back to key (event.key) for character-based shortcuts.
        // If a modifier key is set, the event must match the command registration exactly; if not set, allow either.
        // Dont callback if event.target is text input/area to allow typing in the input.
        if (
          ((codes.length > 0 && codeMatch) || (keys.length > 0 && keyMatch)) &&
          (cmd.ctrlKey !== undefined ? event.ctrlKey === cmd.ctrlKey : true) &&
          (cmd.shiftKey !== undefined ? event.shiftKey === cmd.shiftKey : true) &&
          (cmd.metaKey !== undefined ? event.metaKey === cmd.metaKey : true) &&
          (cmd.altKey !== undefined ? event.altKey === cmd.altKey : true) &&
          !isInput &&
          !isTextarea
        ) {
          cmd.callback(event);
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const register = (cmd: Command) => {
    commandsRef.current.push(cmd);
    return () => {
      commandsRef.current = commandsRef.current.filter(c => c !== cmd);
    };
  };

  return <CommandContext.Provider value={{register}}>{children}</CommandContext.Provider>;
};

export function useRegisterKeyboardCommand(cmd: Command) {
  const ctx = useContext(CommandContext);
  useEffect(() => {
    if (!ctx) return;
    return ctx.register(cmd);
  }, [cmd, ctx]);
}
